import React, { useEffect, useMemo, useState } from 'react';
import {
  EventCountdownComponentFragment,
  JsonDataBlobComponentFragment,
  PageContainerBySlugQuery,
  TextComponentFragment,
  TravelMapComponentFragment,
  WebsiteEditorMediaGalleryComponentFragment,
  WebsiteEditorPhotoComponentFragment,
  WebsiteEditorVideoComponentFragment,
  useReorderPageComponentsMutation,
} from '@graphql/generated';
import { sortBySortOrder } from '@shared/utils/array';
import SortableList from '@shared/components/SortableList';
import { Flex, useToast } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { CheckIcon } from '@assets/icons';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { EditorPageConfig } from '@website-editor/WebsiteEditor.types';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { MediaComponentsAccordion } from './MediaComponentsAccordion';
import { JsonComponentSwitch } from '@website-custom-components/JsonComponentSwitch';
import { doesComponentExist } from '@website-custom-components/useGetJSONComponents';
import { Accommodations } from './Accommodations';
import { RichText } from './RichText';
import { Photo } from './Photo';
import { Video } from './Video';
import { PhotoGallery } from './PhotoGallery';

const LIST_EDIT_ITEM_HEIGHT = 136;

/**
 * Component types that should be excluded from custom page components.
 * These don't have sortOrder or aren't valid custom page components.
 * RsvpAndUnlockGuestDetailsButtonComponent is now handled as a JSON component.
 * TODO: Remove this once these components are removed from the BE response.
 */
const INVALID_COMPONENT_TYPES = [
  'GreetingMessageComponent',
  'EventDisplayNameComponent',
  'RsvpAndUnlockGuestDetailsButtonComponent',
  'EventCountdownComponent',
  'EventDetailsComponent',
] as const;

export enum CustomComponentsWrapperType {
  SingleComponent = 'singleComponent',
  EditComponentList = 'editComponentList',
  AddComponentList = 'addComponentList',
}
export interface CustomComponentsSortableProps
  extends Readonly<{
    dragItemId: string | null;
    isDragActive: boolean;
    itemSize?: number;
  }> {}
export interface CustomComponentsProps
  extends Readonly<{
    pageSlug: string;
    eventHandle: string;
    containerId: string;
    wrapperType?: CustomComponentsWrapperType;
    sortable?: CustomComponentsSortableProps;
    pageConfig: EditorPageConfig;
  }> {}

type RawPageComponent = NonNullable<NonNullable<PageContainerBySlugQuery['pageContainerBySlug']>['components']>[0];

type PagesToExclude =
  | { __typename?: 'EventDisplayNameComponent' }
  | { __typename?: 'GreetingMessageComponent' }
  | { __typename?: 'RsvpAndUnlockGuestDetailsButtonComponent' }
  | { __typename?: 'EventCountdownComponent' }
  | { __typename?: 'EventDetailsComponent' };

type PageComponent = Exclude<RawPageComponent, PagesToExclude>;

/**
 * Generic type guard for filtering out invalid component types.
 * Use this when working with component arrays from different GraphQL queries.
 */
export const filterInvalidComponents = <T extends { __typename?: string }>(component: T): component is Exclude<T, PagesToExclude> => {
  return !INVALID_COMPONENT_TYPES.includes(component.__typename as (typeof INVALID_COMPONENT_TYPES)[number]);
};

interface ComponentRenderProps
  extends Readonly<{
    componentData: PageComponent;
    index: number;
    restProps: Omit<CustomComponentsProps, 'components' | 'pageConfig'>;
    pageConfig: EditorPageConfig;
    firstOfTypeTextIndex: number;
    totalComponents: number;
  }> {}

const renderAccommodations = (props: ComponentRenderProps): JSX.Element => {
  const { componentData, restProps, pageConfig } = props;
  return (
    <Accommodations
      key={componentData.id}
      {...restProps}
      pageConfig={pageConfig}
      componentData={componentData as TravelMapComponentFragment}
    />
  );
};

const renderText = (props: ComponentRenderProps): JSX.Element => {
  const { componentData, index, restProps, pageConfig, firstOfTypeTextIndex, totalComponents } = props;
  return (
    <RichText
      key={componentData.id}
      {...restProps}
      componentData={componentData as TextComponentFragment}
      isFirstOfType={index === firstOfTypeTextIndex}
      isLastItem={index + 1 === totalComponents}
      pageConfig={pageConfig}
    />
  );
};

const renderPhoto = (props: ComponentRenderProps): JSX.Element => {
  const { componentData, index, restProps, pageConfig, totalComponents } = props;
  return (
    <Photo
      key={componentData.id}
      {...restProps}
      componentData={componentData as WebsiteEditorPhotoComponentFragment}
      isLastItem={index + 1 === totalComponents}
      pageConfig={pageConfig}
    />
  );
};

const renderVideo = (props: ComponentRenderProps): JSX.Element => {
  const { componentData, index, restProps, pageConfig, totalComponents } = props;
  return (
    <Video
      key={componentData.id}
      {...restProps}
      componentData={componentData as WebsiteEditorVideoComponentFragment}
      isLastItem={index + 1 === totalComponents}
      pageConfig={pageConfig}
    />
  );
};

const renderPhotoGallery = (props: ComponentRenderProps): JSX.Element => {
  const { componentData, index, restProps, pageConfig, totalComponents } = props;
  return (
    <PhotoGallery
      key={componentData.id}
      {...restProps}
      componentData={componentData as WebsiteEditorMediaGalleryComponentFragment}
      isLastItem={index + 1 === totalComponents}
      pageConfig={pageConfig}
    />
  );
};

const renderJsonDataBlob = (props: ComponentRenderProps): JSX.Element | null => {
  const { componentData, restProps, pageConfig } = props;
  const jsonDataBlobComponent = componentData as JsonDataBlobComponentFragment;
  if (jsonDataBlobComponent.containerContent && !doesComponentExist(jsonDataBlobComponent.containerContent.type)) {
    return null;
  }
  return (
    <JsonComponentSwitch
      key={componentData.id}
      {...restProps}
      componentData={jsonDataBlobComponent}
      pageConfig={pageConfig}
    />
  );
};

const renderComponent = (
  componentData: PageComponent,
  index: number,
  restProps: Omit<CustomComponentsProps, 'components' | 'pageConfig'>,
  pageConfig: EditorPageConfig,
  firstOfTypeTextIndex: number,
  totalComponents: number,
): JSX.Element | null => {
  const renderProps: ComponentRenderProps = {
    componentData,
    index,
    restProps,
    pageConfig,
    firstOfTypeTextIndex,
    totalComponents,
  };

  switch (componentData.__typename) {
    case 'TravelMapComponent':
      return renderAccommodations(renderProps);
    case 'TextComponent':
      return renderText(renderProps);
    case 'PhotoComponent':
      return renderPhoto(renderProps);
    case 'VideoComponent':
      return renderVideo(renderProps);
    case 'MediaGalleryComponent':
      return renderPhotoGallery(renderProps);
    case 'JsonDataBlobComponent':
      return renderJsonDataBlob(renderProps);
    default:
      return null;
  }
};

export const getCustomComponents = (
  props: CustomComponentsProps & {
    components: NonNullable<NonNullable<PageContainerBySlugQuery['pageContainerBySlug']>['components']>;
    photoVideoGalleryTitle?: string;
  },
): JSX.Element[] => {
  const { components: rawComponents, pageConfig, wrapperType, photoVideoGalleryTitle, ...restProps } = props;

  const components = rawComponents.filter(filterInvalidComponents);
  const sortedComponents = sortBySortOrder(components);
  const firstOfTypeTextIndex = sortedComponents.findIndex(component => component.__typename === 'TextComponent');

  // Ensure wrapperType is included in restProps for components
  const restPropsWithWrapperType = { ...restProps, wrapperType };

  // Only group media components into accordion when in AddComponentList mode
  // For SingleComponent and EditComponentList, render all components normally
  if (wrapperType === CustomComponentsWrapperType.AddComponentList) {
    const categorized = sortedComponents.reduce(
      (acc, componentData, index) => {
        const typename = componentData.__typename;
        if (typename === 'TextComponent') {
          const rendered = renderComponent(componentData, index, restPropsWithWrapperType, pageConfig, firstOfTypeTextIndex, components.length);
          if (rendered) acc.textComponents.push(rendered);
        } else if (typename === 'PhotoComponent') {
          // Store photo component for accordion - do NOT render individually in AddComponentList mode
          acc.photoComponent = componentData as WebsiteEditorPhotoComponentFragment;
        } else if (typename === 'VideoComponent') {
          // Store video component for accordion - do NOT render individually in AddComponentList mode
          acc.videoComponent = componentData as WebsiteEditorVideoComponentFragment;
        } else if (typename === 'MediaGalleryComponent') {
          // Store photo gallery component for accordion - do NOT render individually in AddComponentList mode
          acc.photoGalleryComponent = componentData as WebsiteEditorMediaGalleryComponentFragment;
        } else {
          // Render all other components normally with AddComponentList wrapperType
          const rendered = renderComponent(componentData, index, restPropsWithWrapperType, pageConfig, firstOfTypeTextIndex, components.length);
          if (rendered) acc.otherComponents.push(rendered);
        }
        return acc;
      },
      {
        textComponents: [] as JSX.Element[],
        otherComponents: [] as JSX.Element[],
        photoComponent: null as WebsiteEditorPhotoComponentFragment | null,
        videoComponent: null as WebsiteEditorVideoComponentFragment | null,
        photoGalleryComponent: null as WebsiteEditorMediaGalleryComponentFragment | null,
      },
    );

    // Only create accordion when all three media components exist
    const accordionItem =
      categorized.photoComponent && categorized.videoComponent && categorized.photoGalleryComponent ? (
        <MediaComponentsAccordion
          photoComponent={categorized.photoComponent}
          videoComponent={categorized.videoComponent}
          photoGalleryComponent={categorized.photoGalleryComponent}
          photoVideoGalleryTitle={photoVideoGalleryTitle || 'Photo / Video / Gallery'}
          customComponentsProps={{ ...restPropsWithWrapperType, pageConfig }}
        />
      ) : null;

    // Combine components: Text first, then accordion, then other components
    // PlaceToStay ordering is handled in useGetJSONComponents
    return [...categorized.textComponents, accordionItem, ...categorized.otherComponents].filter((item): item is JSX.Element => item !== null);
  }

  // For SingleComponent and EditComponentList modes, render all components normally
  // Photo, Video, and PhotoGallery will render as individual components with their own wrapperType handling
  return sortedComponents
    .map((componentData, index) => renderComponent(componentData, index, restPropsWithWrapperType, pageConfig, firstOfTypeTextIndex, components.length))
    .filter((element): element is JSX.Element => element !== null);
};

type CustomPageComponent =
  | WebsiteEditorPhotoComponentFragment
  | WebsiteEditorVideoComponentFragment
  | TextComponentFragment
  | TravelMapComponentFragment
  | WebsiteEditorMediaGalleryComponentFragment
  | EventCountdownComponentFragment
  | JsonDataBlobComponentFragment;

export const CustomComponents: React.FC<CustomComponentsProps & { components: CustomPageComponent[] }> = props => {
  const { wrapperType, pageSlug, eventHandle, containerId, components: componentsInitial, pageConfig } = props;

  /**
   * Drag-Drop sorting
   */
  const { toast } = useToast();
  const [components, setComponents] = useState(componentsInitial);
  const { tq } = useTranslation('websiteEditor');
  const [isDragActive, setIsDragActive] = useState(false);
  const [dragItemId, setDragItemId] = useState<string | null>(null);
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const telemetry = useWebsiteEditorCustomPageTelemetry();
  const [reorderPageComponents] = useReorderPageComponentsMutation({
    onCompleted: () => {
      toast(`${tq`Your component order has been updated.`}`, { icon: <CheckIcon /> });
    },
    onError: () => {
      toast(`${tq`An error occurred while updating component order. Please try again.`}`, { icon: <WarningTriangle /> });
    },
  });

  const handleDragEnd = useEventCallback((dragData: { fromIndex: number; toIndex: number; items: CustomPageComponent[]; reorderedItem: CustomPageComponent | undefined }) => {
    setDragItemId(null);
    setIsDragActive(false);
    const hasSortOrderChanged = !dragData.items
      .filter((item): item is CustomPageComponent => item !== undefined)
      .every((item, index) => item.sortOrder === components[index].sortOrder);
    // If the sort order has changed, update the sort order in the backend
    if (hasSortOrderChanged) {
      const sortedComponentIds: { id: string }[] = dragData.items.map(itemData => ({ id: itemData.id }));
      reorderPageComponents({
        variables: {
          pageContainerId: containerId,
          components: sortedComponentIds,
        },
        refetchQueries: ['PageContainerBySlug'],
      }).then(() => {
        telemetry.websitePageComponentUpdated(props.pageSlug, 'Reorder', { action: 'reordered', id: dragData.reorderedItem?.id, sortedComponentIds });
        updateWebsitePreview();
      });
    }
  });

  const handleDragStart = useEventCallback((activeId: string) => {
    setDragItemId(activeId);
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    setDragItemId(null);
    setIsDragActive(false);
  });

  const handleChangeOrder = useEventCallback((componentsUpdated: CustomPageComponent[]) => {
    setComponents(componentsUpdated.map((component, index) => ({ ...component, sortOrder: index })));
  });
  useEffect(() => {
    setComponents(componentsInitial);
  }, [componentsInitial]);

  /**
   * Get custom componets list to render
   */
  const customComponents = useMemo(() => {
    return getCustomComponents({
      pageSlug,
      eventHandle,
      containerId,
      components: components,
      wrapperType,
      sortable: { dragItemId, isDragActive, itemSize: LIST_EDIT_ITEM_HEIGHT },
      pageConfig,
      photoVideoGalleryTitle: tq`Photo / Video / Gallery`,
    });
  }, [pageSlug, eventHandle, containerId, components, wrapperType, dragItemId, isDragActive, pageConfig, tq]);

  return (
    <>
      {wrapperType === CustomComponentsWrapperType.EditComponentList ? (
        <Flex
          flexDirection="column"
          marginTop={6}
        >
          <SortableList
            items={components}
            renderItem={(_, index) => customComponents[index]}
            hasDragHandle={true}
            renderVirtualList={true}
            itemSize={LIST_EDIT_ITEM_HEIGHT}
            onChange={handleChangeOrder}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDragCancel={handleDragCancel}
          />
        </Flex>
      ) : (
        <>
          {customComponents.map((component, index) => (
            <React.Fragment key={`custom-components-${index}`}>{component}</React.Fragment>
          ))}
        </>
      )}
    </>
  );
};
